import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "antd";
import dayjs from "dayjs";

import { fetchCapacity } from "../../../../core/data/redux/projects/thunks";
import {
  selectCapacity,
  selectCapacityLoading,
  selectCurrentProject,
  selectMembers,
} from "../../../../core/data/redux/projects/selectors";

import type { AppDispatch } from "../../../../core/data/redux/store";

interface Props {
  startDate: Date;
  endDate: Date;
}

const CELL_SIZE = 14;
const CELL_GAP = 3;
const ROW_LABEL_WIDTH = 28;
const MONTH_LABEL_HEIGHT = 14;
const WEEKDAY_LABELS = ["Mon", "Wed", "Fri"];
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const colorForCount = (count: number): string => {
  if (count <= 0) return "#dbe4ee";
  if (count === 1) return "#bfdbfe";
  if (count === 2) return "#60a5fa";
  if (count === 3) return "#2563eb";
  return "#ef4444";
};

const CapacityPlanner: React.FC<Props> = ({ startDate, endDate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const capacityData = useSelector(selectCapacity);
  const loading = useSelector(selectCapacityLoading);
  const members = useSelector(selectMembers);

  useEffect(() => {
    if (project?.id) {
      dispatch(fetchCapacity(project.id));
    }
  }, [dispatch, project?.id]);

  const userHeatmaps = useMemo(() => {
    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).endOf("day");

    return capacityData.map((cap) => {
      const member: any = members.find((m: any) => m.user_id === cap.user_id);
      const name = member?.user_snapshot?.name || member?.user_name || "Unassigned";
      const img = member?.user_snapshot?.img || null;

      const cells: { date: string; count: number }[] = [];
      let curr = start.clone();
      while (curr.isBefore(end) || curr.isSame(end, "day")) {
        let count = 0;
        for (const issue of cap.issues) {
          if (!issue.start_date || !issue.due_date) continue;
          const iStart = dayjs(issue.start_date).startOf("day");
          const iEnd = dayjs(issue.due_date).endOf("day");
          if ((curr.isAfter(iStart) || curr.isSame(iStart, "day")) && (curr.isBefore(iEnd) || curr.isSame(iEnd, "day"))) {
            count += 1;
          }
        }
        cells.push({ date: curr.format("YYYY-MM-DD"), count });
        curr = curr.add(1, "day");
      }

      return {
        user_id: cap.user_id,
        name,
        img,
        cells,
        totalIssues: cap.total_issues,
        totalStoryPoints: cap.total_story_points,
      };
    });
  }, [capacityData, startDate, endDate, members]);

  const totalDays = useMemo(() => {
    return Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY) + 1);
  }, [startDate, endDate]);

  const totalWeeks = Math.ceil(totalDays / 7);
  const gridWidth = ROW_LABEL_WIDTH + totalWeeks * (CELL_SIZE + CELL_GAP);
  const gridHeight = MONTH_LABEL_HEIGHT + 7 * (CELL_SIZE + CELL_GAP);

  const monthLabels = useMemo(() => {
    const labels: { week: number; label: string }[] = [];
    let lastMonth = -1;
    const start = dayjs(startDate);
    for (let w = 0; w < totalWeeks; w++) {
      const d = start.add(w * 7, "day");
      if (d.month() !== lastMonth) {
        labels.push({ week: w, label: d.format("MMM") });
        lastMonth = d.month();
      }
    }
    return labels;
  }, [startDate, totalWeeks]);

  return (
    <Card
      title={
        <span className="d-flex align-items-center gap-2">
          <i className="ti ti-chart-bar" style={{ color: "#3b82f6", fontSize: 18 }} />
          <span>Team Capacity (Heatmap)</span>
        </span>
      }
      bordered={false}
      loading={loading}
      className="mt-4 shadow-sm"
      style={{ borderRadius: 12 }}
    >
      {userHeatmaps.length === 0 ? (
        <div className="text-center text-muted p-4">No capacity data available</div>
      ) : (
        userHeatmaps.map((uh) => (
          <div key={uh.user_id || "unassigned"} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 fw-semibold" style={{ fontSize: 13 }}>{uh.name}</h6>
              <small className="text-muted" style={{ fontSize: 11 }}>
                {uh.totalIssues} Issues · {uh.totalStoryPoints} Pts
              </small>
            </div>
            <div className="capacity-heatmap-scroll">
              <svg
                width={gridWidth}
                height={gridHeight}
                viewBox={`0 0 ${gridWidth} ${gridHeight}`}
                style={{ display: "block" }}
              >
                {monthLabels.map((m) => (
                  <text
                    key={`m-${m.week}`}
                    x={ROW_LABEL_WIDTH + m.week * (CELL_SIZE + CELL_GAP)}
                    y={MONTH_LABEL_HEIGHT - 4}
                    style={{ fontSize: 9, fill: "#64748b", fontWeight: 600 }}
                  >
                    {m.label}
                  </text>
                ))}
                {WEEKDAY_LABELS.map((label, idx) => {
                  const dayRow = idx === 0 ? 0 : idx === 1 ? 2 : 4;
                  return (
                    <text
                      key={label}
                      x={0}
                      y={MONTH_LABEL_HEIGHT + dayRow * (CELL_SIZE + CELL_GAP) + CELL_SIZE / 2 + 3}
                      style={{ fontSize: 8, fill: "#94a3b8" }}
                    >
                      {label}
                    </text>
                  );
                })}
                {uh.cells.map((c, i) => {
                  const dayOfWeek = (dayjs(c.date).day() + 6) % 7;
                  const week = Math.floor(i / 7) + (dayOfWeek === 0 && i > 0 ? 0 : 0);
                  const weekIdx = Math.floor((i + (dayjs(startDate).day() + 6) % 7) / 7);
                  return (
                    <rect
                      key={c.date}
                      x={ROW_LABEL_WIDTH + weekIdx * (CELL_SIZE + CELL_GAP)}
                      y={MONTH_LABEL_HEIGHT + dayOfWeek * (CELL_SIZE + CELL_GAP)}
                      width={CELL_SIZE}
                      height={CELL_SIZE}
                      rx={2}
                      ry={2}
                      fill={colorForCount(c.count)}
                    >
                      <title>{`${c.date}: ${c.count} issue${c.count === 1 ? "" : "s"}`}</title>
                    </rect>
                  );
                })}
              </svg>
            </div>
          </div>
        ))
      )}
      <style>{`
        .capacity-heatmap-scroll {
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 4px;
        }
        .capacity-heatmap-scroll::-webkit-scrollbar { height: 8px; }
        .capacity-heatmap-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 8px;
        }
        .capacity-heatmap-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 8px;
        }
      `}</style>
    </Card>
  );
};

export default CapacityPlanner;
